import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  registerToastEventListener,
  type ToastEventDetail,
} from '@utils/notify';
import './AppToastProvider.less';

interface ToastItem extends ToastEventDetail {
  id: number;
}

const DEFAULT_DURATION_MS = 4200;
const MAX_TOASTS = 4;

const AppToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timeoutIds = useRef<number[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    const handleToast = (event: Event) => {
      const detail = (event as CustomEvent<ToastEventDetail>).detail;
      if (!detail?.message?.trim()) return;

      const id = Date.now() + Math.random();
      const nextToast: ToastItem = {
        ...detail,
        message: detail.message.trim(),
        id,
      };

      setToasts((current) => [
        ...current.filter((toast) => toast.message !== nextToast.message),
        nextToast,
      ].slice(-MAX_TOASTS));

      const timeoutId = window.setTimeout(
        () => dismiss(id),
        detail.durationMs ?? DEFAULT_DURATION_MS,
      );
      timeoutIds.current.push(timeoutId);
    };

    const unregisterToastEventListener = registerToastEventListener(handleToast);

    return () => {
      unregisterToastEventListener();
      timeoutIds.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutIds.current = [];
    };
  }, [dismiss]);

  return (
    <>
      {children}
      <div className="app-toast-region" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`app-toast app-toast--${toast.level}`}
            role={toast.level === 'warning' || toast.level === 'error' ? 'alert' : 'status'}
          >
            <span className="app-toast__message">{toast.message}</span>
            <button
              type="button"
              className="app-toast__close"
              aria-label="Close notification"
              onClick={() => dismiss(toast.id)}
            >
              x
            </button>
          </div>
        ))}
      </div>
    </>
  );
};

export default AppToastProvider;
